import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { ArrowLeft, FileDown, Truck, User, Calendar, ClipboardCheck } from 'lucide-react';

function InspectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInspection();
  }, [id]);

  const fetchInspection = async () => {
    try {
      const response = await axios.get(`${API}/inspections/${id}`);
      setInspection(response.data);
    } catch (error) {
      toast.error('Failed to load inspection details');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/export/pdf/${id}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inspection_${inspection.plate_number}_${inspection.inspection_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to export to PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading inspection details...</div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-gray-500 mb-4">Inspection not found</p>
        <Button onClick={() => navigate('/reports')}>Back to Reports</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="inspection-detail-page">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/reports')}
            variant="outline"
            data-testid="back-button"
            className="h-10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Inspection Details</h1>
            <p className="text-gray-600 mt-1">Plate Number: {inspection.plate_number}</p>
          </div>
        </div>
        <Button
          onClick={handleExportPDF}
          data-testid="export-pdf-button"
          className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6"
        >
          <FileDown className="w-4 h-4 mr-2" />
          Export PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
            <CardTitle className="flex items-center text-lg">
              <Truck className="w-5 h-5 mr-2 text-blue-600" />
              Vehicle Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 font-medium">Plate Number</p>
              <p className="text-lg font-semibold text-gray-900" data-testid="detail-plate-number">{inspection.plate_number}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Driver Name</p>
              <p className="text-lg font-semibold text-gray-900" data-testid="detail-driver-name">{inspection.driver_name}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
            <CardTitle className="flex items-center text-lg">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              Inspection Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 font-medium">Inspector Name</p>
              <p className="text-lg font-semibold text-gray-900" data-testid="detail-inspector-name">{inspection.inspector_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Inspection Date</p>
              <p className="text-lg font-semibold text-gray-900" data-testid="detail-inspection-date">{inspection.inspection_date}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className={`border-2 shadow-lg ${
        inspection.overall_status === 'Pass' ? 'border-green-200 bg-green-50' :
        inspection.overall_status === 'Service Required' ? 'border-yellow-200 bg-yellow-50' :
        'border-red-200 bg-red-50'
      }`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Overall Status</p>
              <span className={`text-2xl font-bold ${
                inspection.overall_status === 'Pass' ? 'text-green-700' :
                inspection.overall_status === 'Service Required' ? 'text-yellow-700' :
                'text-red-700'
              }`} data-testid="detail-overall-status">                {inspection.overall_status}
              </span>
            </div>
            <ClipboardCheck className={`w-12 h-12 ${
              inspection.overall_status === 'Pass' ? 'text-green-600' :
              inspection.overall_status === 'Service Required' ? 'text-yellow-600' :
              'text-red-600'
            }`} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
          <CardTitle>Inspection Checklist</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Accordion type="single" collapsible className="w-full" data-testid="inspection-sections-accordion">
            {inspection.sections.map((section, sectionIndex) => (
              <AccordionItem key={sectionIndex} value={`section-${sectionIndex}`}>
                <AccordionTrigger className="text-left hover:no-underline" data-testid={`accordion-trigger-${sectionIndex}`}>
                  <span className="font-semibold text-gray-900">{section.section_name}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    {section.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        data-testid={`section-item-${sectionIndex}-${itemIndex}`}
                        className={`p-4 rounded-lg border-l-4 ${
                          item.condition === 'Baik' ? 'border-l-green-500 bg-green-50' :
                          item.condition === 'Service' ? 'border-l-yellow-500 bg-yellow-50' :
                          'border-l-red-500 bg-red-50'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-blue-600 mb-1">Item {item.item_number}</p>
                            <p className="text-sm text-gray-700 mb-2">{item.description}</p>
                            {item.remarks && (
                              <p className="text-xs text-gray-600 italic mt-2">
                                <strong>Remarks:</strong> {item.remarks}
                              </p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                            item.condition === 'Baik' ? 'bg-green-200 text-green-800' :
                            item.condition === 'Service' ? 'bg-yellow-200 text-yellow-800' :
                            'bg-red-200 text-red-800'
                          }`}>
                            {item.condition}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {inspection.general_remarks && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
            <CardTitle>General Remarks</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-700 leading-relaxed" data-testid="general-remarks-display">{inspection.general_remarks}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default InspectionDetail;